export const LAUNCH_READY_PATHS = new Set<string>([
  "/dashboard",
  // Concession approvals queue (admin tier + chairman/director across schools)
  "/concession-approvals",
  // CAMP-2026-0884 — every approval type on one page (same tier and scope).
  "/approvals",
  // Admissions
  "/admissions",
  // People
  "/students",
  "/teachers",
  // Teaching
  "/timetable",
  "/timetable/teacher",
  "/attendance",
  "/substitutes",
  "/gradebook",
  "/homework",
  "/digital-learner",
  "/newsletter",
  "/curriculum",
  "/competency",
  "/exams",
  "/exams/take",
  "/exams/report-cards",
  // WP-7c — the assessment-model pages. This set is an EXACT match, so each nav entry needs its own line
  // or it renders as a disabled "Coming Soon" row (and is hidden outright for teachers).
  "/exams/review-queue",
  "/exams/re-evaluation",
  "/exams/promotion",
  "/exams/co-scholastic",
  // CAMP-2026-0310 — hall-ticket printing (admin tier only; the sidebar
  // entry lives in the Academic group next to Examinations).
  "/hall-tickets",
  // Daily lesson logging — teacher action surface + admin oversight
  "/lessons",
  "/teachers-updates",
  // Finance
  "/finance",
  // Analytics — the network/school analytics dashboards. These have been
  // live for a while (the native bottom tab bar links chairman/director
  // straight to /analytics), but were missing here so the sidebar showed
  // them as "Coming Soon" on desktop and the app drawer dropped them.
  "/analytics",
  "/analytics/ai",
  "/analytics/reports",
  // Communication
  "/communications",
  "/communications/whatsapp",
  "/communication/messages",
  "/communication/notices",
  "/communication/announcements",
  "/meetings",
  // CAMP-2026-0740 — the family-facing certificates list. Without this the
  // parent nav entry is marked "Coming Soon" and then removed outright for
  // non-admin roles, so the issued certificate stays unreachable however
  // correct the route and the API scoping are.
  "/certificates",
  // Recognition core (2026-09-27) — rules, approvals, nominations, insights.
  "/recognition",
  // Badges (2026-09-27) — rebuilt on the predefined catalogue. Without this
  // the nav entry is hidden for families, teachers and staff (exact match).
  "/badges",
  // ID cards (2026-09-27) — the desk for issuers, the holder's own card for
  // everyone else. Exact match, like every entry here.
  "/id-cards",
  "/feedback",
  // Calendar (cross-school events + holidays + exams + meetings)
  "/calendar",
  // HR (teacher self-service)
  "/hr/leave",
  "/hr/payroll",
  "/hr",
  "/hr/performance",
  // Admin
  "/settings",
  "/settings/roles",
  // Audit log viewer — CAMP module audit #5.
  "/audit",
  // CAMP-2026-0571 — personal profile, reachable by every role. This list is
  // an EXACT match, so omitting it left the drawer entry invisible for
  // teachers and parents while working for admins.
  "/settings/profile",
  "/schools",
  // The picked school's health rating (CHAIRMAN / DIRECTOR). This set is an
  // EXACT match, so the nav entry renders as a disabled "Coming Soon" row
  // without it — which is how a shipped module goes missing.
  "/school-health",
  // Studio — the home page (/studio) and the newsletter editor.
  "/studio",
  "/studio/newsletter",
  // Gate + visitor passes (2026-09-27) — their own sections now, not Studio.
  "/gate-passes",
  "/visitor-passes",
  // Event tickets (issuers: events, print sheets, library; TEACHER/STAFF:
  // the gate scanner) and Posters & social posts — recognition-os sections.
  "/event-tickets",
  "/event-tickets/my",
  "/posters",
  // School website (2026-10-01): template, content and live updates for the
  // static school sites (apps/school-website). Admin tier; web only.
  "/website",
  // Hubba — AI chat assistant.
  "/hubba",
  // Brain Arcade — curriculum-aware educational games.
  //
  // isLaunchReadyPath is an EXACT match, not a prefix test, so every nav
  // destination needs its own entry here. Omitting one leaves the item
  // invisible for teachers and students while still working for admins,
  // which reads as "the feature is broken for everyone but you".
  "/playground",
  "/playground/assign",
  "/playground/analytics",
  "/playground/library",
  // Operations
  "/transport",
  "/compliance",
  // Government reporting
  "/udise",
  // AI Early Warning System
  "/early-warning",
  // Faircopy — handwritten/scanned paper → clean printable document
  "/faircopy",
  // Canteen — cashless campus dining & wallet management
  "/canteen",
  // Scholarships — merit/need scheme management & AI eligibility
  "/scholarships",
  // Automation — rule engine for notifications & workflows
  "/automation",
  // AI Advisor — tutoring, study plans & career guidance
  "/ai-tutor",
  // Library — book catalog, circulation & overdue tracking
  "/library",
  // AI Learning OS — modules, mastery, AI explanations & quizzes
  "/learning",
  // SCORM Content Library — e-learning packages & multimedia
  "/scorm",
  // Adaptive Learning — AI-personalised learning paths
  "/adaptive-learning",
  // My Day — the pre-primary daily experience. BOTH paths must be listed:
  // the parent view and the teacher's Class Daily Update capture screen.
  // Anything absent from this set is marked disabled + "Coming Soon" by
  // markLaunchState(), and for every non-admin role (parents, teachers,
  // students) hideUnavailable then removes it from the sidebar entirely —
  // so a shipped feature is invisible until its path is added here.
  "/my-day",
  "/my-day/class",
  // A driver's whole app (router sends DRIVER here on login). Without it the
  // driver's only nav row was hidden and the web sidebar rendered empty.
  "/drive",
  // P1 module audit (2026-09) — owner-approved batch of working modules that
  // were shipped (route + role-access + API all agree) but never flipped on
  // here, so they rendered as disabled "Coming Soon" rows.
  //
  // DPDP — a family's legal right to see how their data is handled, not
  // just an admin compliance tool.
  "/dpdp",
  // PTM — parent-teacher meeting booking.
  "/ptm",
  // HR — teacher/staff self-service + the admin HR module (see /hr/leave,
  // /hr/payroll above, already launch-ready).
  "/hr/performance",
  // Academic Setup wizard (classes/subjects/periods/academic year).
  "/settings/classes-setup",
  // Finance — accounting ledger, the accounts sub-view, and budgeting.
  "/accounting",
  "/finance/accounts",
  "/budget",
  // Operations & wellbeing modules.
  "/hostel",
  "/counselling",
  "/counselling/my",
  "/cpd",
  "/hpcard",
  "/special-education",
  "/sel",
  // Health & Safety — clinic/medical records. Staff/admin only: the API
  // (apps/api/src/routes/health/index.ts) authorizes CHAIRMAN, ADMIN,
  // DIRECTOR, PRINCIPAL, STAFF and nobody else, and lib/role-access.ts keeps
  // STUDENT/PARENT off this prefix, so flipping this on globally does not
  // hand families a page whose API would 403 them.
  "/health",
  "/biometric",
  // Community modules.
  "/alumni",
  "/forums",
  "/surveys",
  // Donations & Fundraising (2026-09-30) — every screen of the module is an
  // exact path here (the set is EXACT-match): the sidebar group lists all of
  // them for admin tier + STAFF, and non-admins reach give / campaigns /
  // checkout / impact. Keep in step with lib/donations/sections.ts
  // (DONATION_LAUNCH_PATHS) — donations-nav.test.ts checks both ways.
  "/donations",
  "/donations/campaigns",
  "/donations/campaigns/new",
  "/donations/register",
  "/donations/donors",
  "/donations/approvals",
  "/donations/pledges",
  "/donations/recurring",
  "/donations/receipts",
  "/donations/reconciliation",
  "/donations/funds",
  "/donations/projects",
  "/donations/scholarships",
  "/donations/sponsorships",
  "/donations/csr",
  "/donations/in-kind",
  "/donations/communications",
  "/donations/impact",
  "/donations/reports",
  "/donations/ai",
  "/donations/settings",
  "/donations/give",
  "/donations/checkout",
  // Academic tools.
  "/plagiarism",
  "/settings/dashboard-builder",
  // Customer Support: raise a request, read our reply (every school role).
  "/support",
  // The platform team's QA board (CONTROL_CENTER + CHAIRMAN; router RequireRole).
  "/tickets",
  // Student items.
  "/science-lab",
  "/career",
  "/badges",
]);

export function isLaunchReadyPath(path: string): boolean {
  return LAUNCH_READY_PATHS.has(path);
}
